import {
  base_colorContentBorder,
  base_colorContentBorderSecondary,
  base_colorRaisedSurface,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_family_weight_medium,
  font_lg,
  font_md,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

import { paddingHorizontal } from '../../billPayFlyoutChrome.style'

/* The live-region helper is shared chrome — both Bill Pay flyouts announce
   their in-flight save the same way. Re-exported so this step's own style
   module stays the single import site for the component. */
export { visuallyHidden } from '../../billPayFlyoutChrome.style'

/*
 * Step 2 "Review and Pay" (EX-40563). Transcribed from the invoice-pay
 * prototype's `ReviewStep.tsx` (isTwoStepFlow / MVP branch) into ext-ui +
 * styled-components, matching Step 1's patterns in this folder and M1's
 * BillPayBankAccounts card treatment.
 *
 * The outer column separates the (optional) alert, the "You're paying" hero,
 * and the review card by spacing(6) (24px). The scroll region carries the same
 * spacing(6)/paddingHorizontal inset Step 1's field list uses.
 */
export const reviewScroll = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
  padding: ${spacing(6)} ${paddingHorizontal};
`

/*
 * Block 1 — the "You're paying" hero. Deliberately NOT a titled content card
 * (it reads fine without inventing a title). Centered column, 1px border,
 * 8px radius, 24px horizontal / 32px vertical padding.
 */
export const hero = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing(1)};
  width: 100%;
  text-align: center;
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${cornerRadius};
  padding: ${spacing(8)} ${spacing(6)};
`

export const heroLabel = css`
  ${font_md}
  color: ${text_colorTextSecondary};
`

/* The 40px / 44px amount is larger than any ext-ui font token, so it is set
   explicitly (spec: "set it explicitly"). Medium weight, primary color. */
export const heroAmount = css`
  font-family: ${font_family_weight_medium};
  font-weight: 500;
  font-size: 40px;
  line-height: 44px;
  color: ${text_colorTextPrimary};
`

export const heroVendor = css`
  ${font_lg}
  font-family: ${font_family_weight_medium};
  font-weight: 500;
  color: ${text_colorTextPrimary};
`

/*
 * Block 2 — the titled review card. Hand-built (NOT ext-ui's `ContentCard`,
 * which is a raised, box-shadowed surface), copying M1's shipped card: a `card`
 * shell (radius + overflow + width) plus a distinct `cardBody` (40px padding).
 */
export const card = css`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  /* Load-bearing, and specifically because of the overflow: hidden above.
     That waives min-height: auto's automatic minimum size, so this card alone
     among reviewScroll's children can collapse — and because it shrinks
     instead of overflowing, the scroll region never gets a scrollbar and the
     rows become unreachable rather than merely off-screen. Only add this to a
     new child here if that child also clips its own overflow. */
  flex-shrink: 0;
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${cornerRadius};
  background: ${base_colorRaisedSurface};
`

export const cardBody = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(5)};
  /* 40px, per the prototype. Note the flyout body already insets by
     paddingHorizontal (40px), so row text lands ~80px from the flyout edge —
     called out in the PR's deviations note. */
  padding: ${spacing(10)};
`

export const cardTitle = css`
  margin: 0;
  ${font_2xl}
  font-family: ${font_family_weight_medium};
  font-weight: 500;
  color: ${text_colorTextPrimary};
`

/* The six-row label/value list. */
export const rowList = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`

/* 1px full-width divider BETWEEN rows only (never after the last). */
export const rowDivider = css`
  height: 1px;
  width: 100%;
  flex-shrink: 0;
  background-color: ${base_colorContentBorderSecondary};
`

export const row = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(4)};
  padding-block: ${spacing(3)};
`

/* Label + optional tooltip glyph. Label 14px secondary; shrink-0 so the value
   column absorbs the remaining width. */
export const rowLabel = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(1)};
  flex-shrink: 0;
  ${font_md}
  color: ${text_colorTextSecondary};
`

/* Value: right-aligned, 16px primary — deliberately the LARGER of the pair. */
export const rowValue = css`
  min-width: 0;
  text-align: right;
  ${font_lg}
  color: ${text_colorTextPrimary};
`

/* Warning alert wrapper (block 0). The ext-ui AlertPanel has no actions slot,
   so the "Add bank account" CTA is rendered as a sibling below the panel. */
export const alertWrap = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  align-items: flex-start;
`

/* Title + body composed into the AlertPanel's single children (subtitle) slot
   — it renders `title` only as a native hover attribute, not a visible head. */
export const alertContent = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
`

export const alertTitle = css`
  font-family: ${font_family_weight_medium};
  font-weight: 500;
`

/* The acknowledgement lives in the sticky footer (design instruction). The
   whole row is a <label> so clicking the copy toggles the checkbox. */
export const ackRow = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing(2)};
  cursor: pointer;
`

/* 14px PRIMARY, not secondary — the prototype's own
   `text-[14px] text-text-color-text-primary`. This is the copy the user is
   actually agreeing to, so it reads at body weight rather than as a hint. */
export const ackCopy = css`
  ${font_md}
  color: ${text_colorTextPrimary};
`
